import React, { useState, useEffect } from 'react';
import { useAuth } from './context/AuthContext';
import { AuthProvider } from './context/AuthContext';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import ContactsPage from './components/ContactsPage';
import DashboardPage from './components/DashboardPage';
import LoginPage from './components/LoginPage';
import RegisterPage from './pages/RegisterPage';
import SettingsPage from './components/SettingsPage';
import MyCompanyPage from './components/MyCompanyPage';
// Removido RequirePermission: rotas sem verificação de permissões, apenas auth
// Removido UsersPage (gestão ficará dentro de Configurações)

// Páginas legadas mapeadas para rotas em PT-BR
import AtendimentosPage from './legacy/components/AtendimentosPage';
import ConnectionsPage from './legacy/components/ConnectionsPage';
import AstroAgentsPage from './legacy/components/AstroAgentsPage';
import MyAgentsPage from './legacy/components/MyAgentsPage';
import AgentsPage from './components/AgentsPage';
import ProjectsPage from './components/ProjectsPage';
import CalendarPage from './pages/CalendarPage';
import HaloAgentsPage from './pages/HaloAgentsPage';
import HaloMobilePage from './pages/HaloMobilePage';
import AdminDashboard from './components/AdminDashboard';
import WebChatWidget from './components/WebChat/WebChatWidget';
import { runCacheSchemaMigration } from './utils/cacheMaintenance';

import './components/MainLayout.css';
import AppIcon from './components/icons/AppIcon';

// ErrorBoundary simples para capturar erros em AppRoutes e evitar quebra total
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('Erro capturado em ErrorBoundary:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 16 }}>
          <h3>Ocorreu um erro na aplicação</h3>
          <pre style={{ whiteSpace: 'pre-wrap' }}>{String(this.state.error || '')}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Componente raiz da aplicação.  Ele define o cabeçalho e delega
 * a responsabilidade de exibir a interface de chat para o componente
 * `ChatPanel`.
 */
// Componente para rotas protegidas usando AuthContext
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Componente para rotas restritas ao plano Admin
const AdminRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Verificar se o usuário tem plano admin
  const isAdmin = user?.plano_conta === 'admin' || user?.is_superuser === true;
  
  if (!isAdmin) {
    return (
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center',
        color: '#9bb3c7'
      }}>
        <AppIcon name="lock" style={{ fontSize: '4rem', color: 'rgba(180, 59, 234, 0.3)' }} />
        <h2 style={{ color: '#ffffff', marginTop: '1rem' }}>Acesso Restrito</h2>
        <p>Esta página está disponível apenas para usuários com plano Admin.</p>
      </div>
    );
  }
  
  return children;
};

// Componente principal do painel
function PanelLayout({ onLogout }) {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Atualiza a aba ativa quando a rota muda
  useEffect(() => {
    const path = location.pathname.split('/')[1]; // Pega o primeiro segmento da rota
    if (path) {
      setActiveTab(path);
    } else {
      setActiveTab('dashboard');
    }
    // Não recolher automaticamente ao navegar
    setIsSidebarCollapsed(false);
  }, [location]);

  const handleSettingsClick = () => {
    setActiveTab('configuracoes');
  };

  const handleTabChange = () => {
    // Sem recolher automaticamente
    setIsSidebarCollapsed(false);
  };

  return (
    <div className="app-container">
      <div className="app-layout">
        <Sidebar 
          onLogout={onLogout}
        />
        <div className="main-layout">
          <Header 
            onSettingsClick={handleSettingsClick} 
          />
          <main className="main-content">
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              {/* Rotas internas (somente autenticado) */}
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/contatos" element={<ContactsPage />} />
              <Route path="/atendimentos" element={<AtendimentosPage />} />
              <Route path="/conexoes" element={<ConnectionsPage />} />
              <Route path="/astro-agentes" element={<AstroAgentsPage />} />
              <Route path="/meus-agentes" element={<AgentsPage />} />
              <Route path="/agentes-halo" element={<AdminRoute><HaloAgentsPage /></AdminRoute>} />
              <Route path="/projetos" element={<ProjectsPage />} />
              <Route path="/calendario" element={<CalendarPage />} />
              <Route path="/minha-empresa" element={<MyCompanyPage />} />
              <Route path="/configuracoes" element={<SettingsPage />} />
              <Route path="/painel-admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </main>
        </div>
      </div>
      <WebChatWidget />
    </div>
  );
};

// Componente principal da aplicação
const App = () => {
  return (
    <AuthProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ErrorBoundary>
          <AppRoutes />
        </ErrorBoundary>
      </Router>
    </AuthProvider>
  );
};

// Componente para gerenciar as rotas e navegação
function AppRoutes() {
  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuth();

  useEffect(() => {
    runCacheSchemaMigration();
  }, []);

  // Função para lidar com o login bem-sucedido
  const handleLogin = (loginResponse) => {
    try {
      if (!loginResponse) {
        throw new Error('Nenhuma resposta de login recebida');
      }
      if (loginResponse.success === true) {
        navigate('/dashboard', { replace: true });
      } else {
        const errorMsg = loginResponse.message || 'Falha na autenticação';
        console.error('Login falhou:', errorMsg);
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('Erro ao processar o login:', error);
      alert(`Falha no login: ${error.message}`);
    }
  };

  // Função para lidar com o logout
  const handleLogout = () => {
    logout();
    navigate('/login');
    // Não recarregamos a página; o contexto já foi limpo
  };

  return (
    <Routes>
      {/* Rota raiz decide com base na autenticação */}
      <Route path="/" element={
        isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
      } />
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage onLogin={handleLogin} />
      } />
      <Route path="/register" element={
        isAuthenticated ? <Navigate to="/dashboard" replace /> : <RegisterPage />
      } />
      {/* Halo Mobile - página standalone com login próprio (PWA) */}
      <Route path="/halo" element={<HaloMobilePage />} />
      <Route path="/*" element={
        <ProtectedRoute>
          <PanelLayout onLogout={handleLogout} />
        </ProtectedRoute>
      } />
      {/* Rota de fallback para qualquer rota não encontrada */}
      <Route path="*" element={<Navigate to="/login" replace />} />

      {/* Removidas as rotas duplicadas protegidas por plano — a proteção e navegação
          agora acontecem apenas dentro do PanelLayout. */}
    </Routes>
  );
};

export default App;